// @flow
import * as React from 'react';
import cx from 'classnames';

import { useOutsideClick } from '../../../utils/use-outside-click';
import findAncestorByCSSProperty from '../../../utils/findAncestorByCSSProperty';
import DropdownItem from './dropdown-item';

import type {
  DropdownInputProps,
  IndexUpdaterFunc,
  OptionRendererFunc,
  Option,
} from './typings';

import cs from './styles.pcss';

export type Props = {
  children?: (props: DropdownInputProps) => React.Node,
  onChange: (value: mixed) => void,
  onKeyDown?: (e: SyntheticEvent<HTMLElement>) => void,
  directionHandlers?: {
    start: IndexUpdaterFunc,
    end: IndexUpdaterFunc,
    next: IndexUpdaterFunc,
    prev: IndexUpdaterFunc,
    pageUp: IndexUpdaterFunc,
    pageDown: IndexUpdaterFunc,
  },
  options?: Array<Option>,
  className?: string,
  dropdownClassName?: string,
  renderOption?: OptionRendererFunc,
  renderEmptyDropdown?: () => React.Node,
  value?: mixed,
  pageSize?: number,
  keyword?: string,
};

const getFocusedValue = (options, value) => {
  const option = options.find((o) => !o.hidden && o.value === value);
  const firstValue =
    options && options[0] && options[0].value ? options[0].value : undefined;
  return option ? value : firstValue;
};

const getFocusableOptions = (options: Array<Option>) =>
  options.filter((o) => o.type !== 'notFound');

const defaultDirectionHandlers = {
  start: () => 0,
  end: (_: number, optionNumder: number) => optionNumder - 1,
  next: (index: number, optionNumder: number) => (index + 1) % optionNumder,
  prev: (index: number, optionNumder: number) =>
    index > 0 ? index - 1 : optionNumder - 1,
  pageUp: (index: number, _: number, pageSize: number) => {
    const potentialIndex = index - pageSize;
    return potentialIndex < 0 ? 0 : potentialIndex;
  },
  pageDown: (index: number, optionNumder: number, pageSize: number) => {
    const potentialIndex = index + pageSize;
    return potentialIndex > optionNumder ? optionNumder - 1 : potentialIndex;
  },
};

export default function Dropdown({
  options = [],
  directionHandlers = defaultDirectionHandlers,
  pageSize = 5,
  renderEmptyDropdown = () => null,
  onKeyDown = () => {},
  renderOption,
  dropdownClassName,
  keyword,
  value,
  children,
  onChange,
  className,
}: Props): React.Node {
  const focusableOptions = getFocusableOptions(options);
  const firstOpen = React.useRef(true);
  const [shown, setShown] = React.useState(false);
  const [focusedValue, setFocusedValue] = React.useState(() =>
    getFocusedValue(focusableOptions, value)
  );
  const [focusedOptionPosition, setFocusedOptionPosition] =
    React.useState(null);
  const [measuredFocusedOption, setMeasuredFocusedOption] =
    React.useState(null);

  const clickRef = React.useRef();
  const wrapper = React.useRef();
  const borderElement = React.useRef();
  const dropdown = React.useRef<?HTMLDivElement>();

  const focusedOptionRef = React.useCallback(
    (node) => {
      if (node) {
        const { top, bottom, height } = node.getBoundingClientRect();
        setFocusedOptionPosition({
          top,
          bottom,
          height,
          offsetHeight: node.offsetHeight,
          offsetTop: node.offsetTop,
        });
        setMeasuredFocusedOption(focusedValue);
      }
    },
    [focusedValue, setFocusedOptionPosition, setMeasuredFocusedOption]
  );

  React.useEffect(() => {
    if (wrapper && wrapper.current) {
      borderElement.current = findAncestorByCSSProperty(
        wrapper.current,
        'overflow',
        'hidden'
      );
    }
  }, []);

  const scrollToFocusedOption = () => {
    if (focusedOptionPosition && dropdown && dropdown.current) {
      const dropdownRect = dropdown.current.getBoundingClientRect();

      // Scroll top to the focused option
      if (
        dropdown.current &&
        focusedOptionPosition.bottom > dropdownRect.bottom
      ) {
        dropdown.current.scrollTop =
          focusedOptionPosition.offsetTop +
          focusedOptionPosition.height -
          dropdown.current.offsetHeight;
      }

      // Scroll bottom to the focused option
      if (dropdown.current && focusedOptionPosition.top < dropdownRect.top) {
        dropdown.current.scrollTop =
          focusedOptionPosition.offsetTop +
          focusedOptionPosition.height -
          focusedOptionPosition.offsetHeight;
      }
    }
  };

  React.useEffect(() => {
    const hasFocusedOption = focusableOptions.some(
      (o) => o.value === focusedValue
    );
    if (!hasFocusedOption) {
      setFocusedValue(getFocusedValue(focusableOptions, value));
    }
  }, [focusableOptions]);

  React.useEffect(() => {
    if (value !== focusedValue) {
      setFocusedValue(getFocusedValue(focusableOptions, value));
    }
  }, [value]);

  React.useEffect(() => {
    if (
      shown &&
      firstOpen.current &&
      dropdown &&
      dropdown.current &&
      focusedOptionPosition
    ) {
      // Scroll to the selected option on dropdown open
      dropdown.current.scrollTop = focusedOptionPosition.offsetTop;
      firstOpen.current = false;
    } else if (measuredFocusedOption) {
      scrollToFocusedOption();
    }
  }, [measuredFocusedOption, shown]);

  const getValueFocusIndex: (value: any) => number = (
    newValue: any
  ): number => {
    const index = focusableOptions.findIndex((o) => o.value === newValue);
    return index === -1 ? 0 : index;
  };

  const toggleDropdown: (showStatus?: boolean) => void = (
    showStatus?: boolean
  ) => {
    const newShown = typeof showStatus === 'boolean' ? showStatus : !shown;

    if (newShown === shown) return;

    firstOpen.current = newShown;

    if (newShown) {
      setFocusedValue(getFocusedValue(focusableOptions, value));
    }

    setShown(newShown);
  };

  useOutsideClick(clickRef, () => toggleDropdown(false));

  const selectFocusedOption: () => void = () => {
    onChange(focusedValue);
    toggleDropdown();
  };

  const focusAdjacentOption = (indexUpdater: IndexUpdaterFunc) => {
    if (!shown) {
      toggleDropdown();
      return;
    }

    if (!focusableOptions.length) return;
    const focusedIndex = indexUpdater(
      getValueFocusIndex(focusedValue),
      focusableOptions.length,
      pageSize
    );

    setFocusedValue(focusableOptions[focusedIndex].value);
  };

  const handleKeyDown: (event: SyntheticKeyboardEvent<HTMLElement>) => void = (
    event: SyntheticKeyboardEvent<HTMLElement>
  ) => {
    if (onKeyDown(event)) {
      return;
    }

    switch (event.keyCode) {
      case 9: // tab
        if (shown) {
          toggleDropdown();
        }
        return;
      case 13: // enter
        if (shown) {
          selectFocusedOption();
        } else {
          toggleDropdown();
        }
        event.stopPropagation();
        break;
      case 27: // escape
        if (shown) {
          toggleDropdown();
          event.stopPropagation();
        }
        break;
      case 38: // up
        focusAdjacentOption(directionHandlers.prev);
        break;
      case 40: // down
        focusAdjacentOption(directionHandlers.next);
        break;
      case 33: // page up
        focusAdjacentOption(directionHandlers.pageUp);
        break;
      case 34: // page down
        focusAdjacentOption(directionHandlers.pageDown);
        break;
      case 35: // end key
        if (event.shiftKey) {
          return;
        }
        focusAdjacentOption(directionHandlers.end);
        break;
      case 36: // home key
        if (event.shiftKey) {
          return;
        }
        focusAdjacentOption(directionHandlers.start);
        break;
      default:
        return;
    }

    event.preventDefault();
  };

  const renderDropdown = (): React.Node => {
    if (!shown) return null;

    const items: React.Node =
      options.length === 0
        ? // Empty dropdown message
          renderEmptyDropdown()
        : // Options
          options.map((option, index) => {
            if (option.type === 'notFound') {
              return (
                <DropdownItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${option.type}-${index}`}
                  option={option}
                  buttonRef={() => {}}
                  focused={false}
                  notFound={option.type === 'notFound'}
                />
              );
            }

            return (
              <DropdownItem
                key={String(option.value)}
                option={option}
                keyword={keyword}
                renderOption={renderOption}
                buttonRef={focusedOptionRef}
                onMouseDown={selectFocusedOption}
                onMouseEnter={setFocusedValue}
                selected={value === option.value}
                focused={
                  option.value === focusedValue ||
                  (focusedValue === undefined && index === 0)
                }
              />
            );
          });

    if (!items) {
      return null;
    }

    return (
      <div className={cx(cs.dropdown, dropdownClassName)}>
        <div ref={dropdown} className={cx(cs.options, cs.stretch)}>
          {items}
        </div>
      </div>
    );
  };

  const renderDropdownInput = (inputProps: any): React.Node => {
    if (children) {
      return children(inputProps);
    }

    console.warn('Provide either children or component to Dropdown'); // eslint-disable-line no-console
    return null;
  };

  return (
    <div ref={clickRef} className={cs.fullWidth}>
      <div
        ref={wrapper}
        className={cx(cs.dropdownWrapper, className, cs.medium)}
      >
        {renderDropdownInput({
          option: options.find((o) => o.value === value),
          value,
          isShown: shown,
          toggleDropdown,
          handleKeyDown,
        })}
        {renderDropdown()}
      </div>
    </div>
  );
}
